/**
 * One delivery drop: chilled, frozen, and ambient can arrive together.
 * Ambient has no temperature. A rejection is this product, or the whole drop.
 */

import {
  FOOD_SAFETY_DELIVERY_REJECTION_CODE,
  FOOD_SAFETY_DELIVERY_REJECTION_LABELS,
  FOOD_SAFETY_DELIVERY_REJECTION_SCOPE,
  FOOD_SAFETY_DELIVERY_REJECTION_SCOPE_LABELS,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_DELIVERY_TEMP_PRESETS,
} from 'src/constants/foodSafety'
import { formatTempValue } from 'src/utils/foodSafetyTempRange'

const BAND_ORDER = [
  FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED,
  FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN,
  FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT,
]

const labelFor = band => FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band]?.label || ''

export function defaultDeliveryBands() {
  return [{ band: FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED, value: null }]
}

export function defaultDeliveryRejectionScope(code) {
  return code === FOOD_SAFETY_DELIVERY_REJECTION_CODE.SPILLED_OR_WET_PACKAGING
    ? FOOD_SAFETY_DELIVERY_REJECTION_SCOPE.DROP
    : FOOD_SAFETY_DELIVERY_REJECTION_SCOPE.PRODUCT
}

export function orderedDeliveryBands(bands) {
  const byBand = new Map(
    (Array.isArray(bands) ? bands : [])
      .filter(row => BAND_ORDER.includes(row?.band))
      .map(row => [row.band, row]),
  )
  return BAND_ORDER.filter(band => byBand.has(band)).map(band =>
    byBand.get(band),
  )
}

export function toggleDeliveryBand(bands, band) {
  const current = orderedDeliveryBands(bands)
  if (current.some(row => row.band === band)) {
    return current.filter(row => row.band !== band)
  }
  return orderedDeliveryBands([...current, { band, value: null }])
}

export function setDeliveryBandTemp(bands, band, value) {
  return orderedDeliveryBands(bands).map(row =>
    row.band === band ? { ...row, value } : row,
  )
}

export function deliveryBandTemp(bands, band) {
  const row = orderedDeliveryBands(bands).find(item => item.band === band)
  return row ? row.value : null
}

/** Names a venue already buys from, for the supplier field. */
export function venueSupplierNames(venueSuppliers) {
  const seen = new Set()
  const names = []
  for (const row of venueSuppliers || []) {
    const name = String(
      row?.supplierTitle || row?.supplierName || row?.title || '',
    ).trim()
    const key = name.toLowerCase()
    if (!name || seen.has(key)) continue
    seen.add(key)
    names.push(name)
  }
  names.sort((a, b) => a.localeCompare(b, 'en'))
  return names
}

/** Names that match the typed supplier field, for a pick-list under it. */
export function filterVenueSupplierNames(names, query, { max = 10 } = {}) {
  const list = Array.isArray(names) ? names : []
  const q = String(query || '')
    .trim()
    .toLowerCase()
  const matched = q
    ? list.filter(name => String(name).toLowerCase().includes(q))
    : list
  if (q && matched.length === 1 && matched[0].toLowerCase() === q) {
    return []
  }
  return matched.slice(0, Math.max(0, Number(max) || 10))
}

export function validateDeliveryDrop(draft) {
  const bands = orderedDeliveryBands(draft?.deliveryBands)
  if (bands.length === 0) {
    return 'Say what arrived: chilled, frozen, or ambient.'
  }
  for (const row of bands) {
    if (row.band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) continue
    if (row.value == null || row.value === '') {
      return `Enter the ${labelFor(row.band).toLowerCase()} temperature.`
    }
  }
  if (draft?.accepted == null) {
    return 'Mark whether the delivery was accepted.'
  }
  if (draft.accepted === false && !draft.rejectionCode) {
    return 'Say why it went back.'
  }
  return null
}

export function isDeliveryDropOutOfRange(bands) {
  return orderedDeliveryBands(bands).some(row => {
    if (row.band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) return false
    if (row.value == null || row.value === '') return false
    const tempMax = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[row.band]?.tempMax
    return tempMax != null && Number(row.value) > Number(tempMax)
  })
}

export function deliveryBandsPayload(bands) {
  return orderedDeliveryBands(bands).map(row => {
    if (row.band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT)
      return { band: row.band }
    return { band: row.band, value: Number(row.value) }
  })
}

export function formatDeliveryBands(payload) {
  const bands = payload?.deliveryBands
  if (!Array.isArray(bands) || bands.length === 0) return ''
  return orderedDeliveryBands(bands)
    .map(row => {
      const label = labelFor(row.band)
      if (
        row.band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT ||
        row.value == null ||
        row.value === ''
      ) {
        return label
      }
      return `${label} ${formatTempValue(Number(row.value))}°C`
    })
    .join(' · ')
}

export function formatDeliveryRejection(payload) {
  if (payload?.accepted !== false) return ''
  const reason =
    FOOD_SAFETY_DELIVERY_REJECTION_LABELS[payload.rejectionCode] || ''
  const scope =
    FOOD_SAFETY_DELIVERY_REJECTION_SCOPE_LABELS[payload.rejectionScope] || ''
  const note = String(payload.rejectionReason || '').trim()
  return [reason, scope, note].filter(Boolean).join(' · ')
}
